import { useMutation } from '@apollo/client';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { ArchiveAudienceDocument } from '../../../../graphql/mutation/audiences/ArchiveAudience.generated.tsx';
import { CreateLogDocument } from '../../../../graphql/mutation/error/createLog.generated.tsx';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { ArchiveAudienceDialogContent } from '../components/ArchiveAudienceDialogContent.tsx';

type OpenArchiveModalParams = {
  audiences: Array<Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'>>;
  onSuccess: () => void;
};

export const useArchiveAudiences = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [pushLog] = useMutation(CreateLogDocument);
  const [archiveAudience, { loading: archiveLoading }] = useMutation(ArchiveAudienceDocument);
  const {
    open: confirmationOpen,
    close: confirmationClose,
    setLoading: confirmationSetLoading,
    toogleConfirmationDisabled,
  } = useConfirmationDialog({ confirmationDisabled: true });

  const archive = useCallback(
    async (audiences: OpenArchiveModalParams['audiences']) => {
      try {
        const response = await archiveAudience({
          variables: { ids: audiences.map(({ id }) => id) },
        });

        const archivedAudiencesCount = response?.data?.audience?.returning.filter(
          ({ is_archived }) => is_archived
        ).length;

        if (response.errors?.length || archivedAudiencesCount === 0) {
          throw new Error();
        }

        if (audiences.length > 1) {
          enqueueSnackbar(
            t('Successfully archived {count} of {total} audiences', {
              count: archivedAudiencesCount,
              total: audiences.length,
            }),
            { variant: 'success' }
          );
        } else {
          enqueueSnackbar(t('Successfully archived audience'), { variant: 'success' });
        }

        return true;
      } catch (_error) {
        const location = window.location.href;
        await pushLog({
          variables: {
            location,
            msg: `Failed to archive audience${audiences.length > 1 ? 's' : ''}`,
            name: currentUser?.displayName,
            email: currentUser?.email,
          },
        });

        if (audiences.length > 1) {
          enqueueSnackbar(t('Failed to archive {total} audiences', { total: audiences.length }), { variant: 'error' });
        } else {
          enqueueSnackbar(t('Failed to archive audience'), { variant: 'error' });
        }

        return false;
      }
    },
    [archiveAudience, enqueueSnackbar, pushLog, t, currentUser]
  );

  const openArchiveModal = useCallback(
    ({ audiences, onSuccess }: OpenArchiveModalParams) => {
      const audienceLabel = audiences.length > 1 ? t('Audiences') : t('Audience');
      const title = t('Archive {audience}', { audience: audienceLabel });

      const onConfirmed = async () => {
        try {
          confirmationSetLoading(true);
          const archivedSuccessfully = await archive(audiences);

          if (archivedSuccessfully) {
            onSuccess();
          }
        } finally {
          confirmationSetLoading(false);
          confirmationClose();
        }
      };

      confirmationOpen({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Archive'),
        content: (
          <ArchiveAudienceDialogContent audiences={audiences} setConfirmationBtnDisabled={toogleConfirmationDisabled} />
        ),
        customContent: true,
        hideConfirm: audiences.length === 0,
        onConfirmed,
        showExtraBtn: false,
        title,
      });
    },
    [archive, confirmationOpen, confirmationClose, confirmationSetLoading, t, toogleConfirmationDisabled]
  );

  return { openArchiveModal, isLoading: archiveLoading };
};
